import { configureStore, combineReducers, Reducer, AnyAction } from '@reduxjs/toolkit';
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';
import authSlice from './authSlice';
import messagesSlice from './messagesSlice';
import usersSlice from './usersSlice';
import uiSlice from './uiSlice';
import notificationSlice from './notificationSlice';
import globalNotificationSlice from './globalNotificationSlice';
import rolesSlice from './rolesSlice';
import unitsSlice from './unitsSlice';
import shiftsSlice from './shiftsSlice';
import patientFeedbackSlice from './patientFeedbackSlice';
import patientSessionSlice from './patientSessionSlice';
import staffFeedbackSlice from './staffFeedbackSlice';
import adminSlice from './adminSlice';
import archiveSlice from './archiveSlice';
import dutyTrackingSlice from './dutyTrackingSlice';
import connectionSlice from './connectionSlice';
import tasksSlice from './tasksSlice';
import menuLayoutSlice from './menuLayoutSlice';
import sidebarThemeSlice from './sidebarThemeSlice';
import dashboardPrefsSlice from './dashboardPrefsSlice';
import { archiveMiddleware } from './archiveMiddleware';

const appReducer = combineReducers({
  auth: authSlice,
  messages: messagesSlice,
  users: usersSlice,
  ui: uiSlice,
  notifications: notificationSlice,
  globalNotifications: globalNotificationSlice,
  roles: rolesSlice,
  units: unitsSlice,
  shifts: shiftsSlice,
  patientFeedback: patientFeedbackSlice,
  patientSession: patientSessionSlice,
  staffFeedback: staffFeedbackSlice,
  admin: adminSlice,
  archive: archiveSlice,
  dutyTracking: dutyTrackingSlice,
  connection: connectionSlice,
  tasks: tasksSlice,
  menuLayout: menuLayoutSlice,
  sidebarTheme: sidebarThemeSlice,
  dashboardPrefs: dashboardPrefsSlice,
});

/**
 * SECURITY: actions that end a user session. On any of these we reset the WHOLE
 * Redux tree to initial state so that NO content slice (messages, patient
 * feedback, staff feedback, archive, tasks, duty tracking, units, users, …)
 * carries the previous user's data into the next session.
 *
 * Logout/login in this app are soft React-Router navigations (no full page
 * reload), and previously only `authSlice` cleared itself on logout — every
 * other slice retained the prior user's rows. This root reducer closes that
 * systemic gap in one place.
 */
const SESSION_RESET_ACTIONS = new Set<string>([
  'auth/logout/fulfilled', // logoutAsync.fulfilled
  'auth/clearAuth',        // manual clear (expired/invalid session)
]);

export const rootReducer: Reducer<ReturnType<typeof appReducer>> = (state, action: AnyAction) => {
  if (state && SESSION_RESET_ACTIONS.has(action.type)) {
    // Preserve only `auth` (its own reducer handles the logout/clear transition,
    // including pendingLogoutRedirect used to drive the redirect) and `ui`
    // (device-level prefs such as theme/layout that should survive logout — the
    // app does not reload, so wiping them would visibly reset the UI). Every
    // other slice is dropped to `undefined`, which makes combineReducers
    // re-initialise it to its initial state.
    state = {
      auth: state.auth,
      ui: state.ui,
    } as ReturnType<typeof appReducer>;
  }
  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
        ignoredActionsPaths: ['payload.file', 'payload.voiceRecording'],
        ignoredPaths: ['patientFeedback.attachedFiles', 'patientFeedback.voiceRecording'],
      },
    }).concat(archiveMiddleware),
});

// Expose store globally for API service access
declare global {
  interface Window {
    store: typeof store;
  }
}

window.store = store;

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
